import { useCallback, useEffect, useState } from "react";
import { Building2, CalendarClock, Hammer, Loader2, Plus, Receipt, Sparkles } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Chip, Empty, Kpi, Loading, PageHeader, Panel, Table, Td, Th } from "@/components/common/Chrome";
import { BookingCalendar } from "@/components/common/BookingCalendar";
import { DateField } from "@/components/common/DateField";
import { BookingFinance } from "@/components/property/BookingFinance";
import { CancelBooking } from "@/components/property/CancelBooking";
import { Hirers } from "@/components/property/Hirers";
import { Invoices } from "@/components/property/Invoices";
import { Utilisation } from "@/components/property/Utilisation";
import { LogTimeDialog } from "@/components/common/LogTimeDialog";
import { HireRequests } from "@/components/property/HireRequests";
import { HireSettings } from "@/components/property/HireSettings";
import { RecurringBooking } from "@/components/property/RecurringBooking";
import { refOf, rowClick, stopRow, useRowDetail } from "@/components/common/DetailSheet";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { dateTime, money } from "@/lib/format";

const OK = "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300";
const WARN = "border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300";
const BAD = "border-red-500/30 bg-red-500/10 text-red-700 dark:text-red-300";
const INFO = "border-primary/40 bg-primary/10 text-primary";

const F = ({ label, children }) => (
  <div className="space-y-1.5"><Label className="text-[11px] text-muted-foreground">{label}</Label>{children}</div>
);

const BLANK_BKG = { space_code: "", title: "", booked_for: "", booking_type: "external",
  starts_at: "", ends_at: "", attendees: 0, contact: "", notes: "" };
const BLANK_JOB = { property_code: "", space_code: "", title: "", category: "repair",
  priority: "medium", reported_by: "", contractor: "", estimated_cost: 0, due_on: "", description: "" };
const BLANK_TASK = { property_code: "", space_code: "", task: "", frequency: "weekly",
  assigned_to: "", due_on: "" };
const BLANK_SPACE = { property_code: "", name: "", room_number: "", space_type: "hall",
  capacity: 0, hourly_rate: 0, day_rate: 0, currency: "INR", bookable: true, notes: "",
  pet_friendly: "not_allowed", smoking_policy: "not_allowed", food_allowed: "allowed",
  fireplace_allowed: "not_available", kids_friendly: "allowed", kids_play_area: "not_available",
  parking_available: "not_available", parking_spaces: 0, parking_included: "not_applicable",
  parking_requires_booking: false, parking_charge: 0, parking_availability: "",
  policy_notes: "" };

export default function Property() {
  const { can } = useAuth();
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState("");
  const [dialog, setDialog] = useState("");
  const [booking, setBooking] = useState(BLANK_BKG);
  const [job, setJob] = useState(BLANK_JOB);
  const [task, setTask] = useState(BLANK_TASK);
  const [space, setSpace] = useState(BLANK_SPACE);
  const [tab, setTab] = useState("bookings");
  const [ledger, setLedger] = useState(null);
  const [completing, setCompleting] = useState(null);
  const [cancelling, setCancelling] = useState(null);
  const [cancelReason, setCancelReason] = useState("");
  const [financeFor, setFinanceFor] = useState(null);
  const [timeForBooking, setTimeForBooking] = useState(null);
  const [cancellingBooking, setCancellingBooking] = useState(null);
  const detail = useRowDetail("", () => load());
  const [completion, setCompletion] = useState({ actual_cost: 0, note: "" });

  const load = useCallback(async () => {
    const [p, b, h, m, hr, hs] = await Promise.all([
      api.get("/property/properties"), api.get("/property/bookings"),
      api.get("/property/housekeeping"), api.get("/property/maintenance"),
      api.get("/hall-hire/requests"), api.get("/hall-hire/settings"),
    ]);
    setState({ props: p.data, bookings: b.data, housekeeping: h.data, maintenance: m.data,
               requests: hr.data, hire: hs.data });
  }, []);

  useEffect(() => { load().catch(() => setState(false)); }, [load]);

  const act = async (key, fn, message) => {
    setBusy(key);
    try {
      const res = await fn();
      if (message) toast.success(typeof message === "function" ? message(res) : message);
      setDialog("");
      await load();
      return res;
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(""); }
  };

  if (!state) return <Loading label="Loading properties and facilities" />;
  const { props, bookings, housekeeping, maintenance, requests, hire } = state;

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="SCR-025 · Property & facility management"
        title="Halls that earn, rooms that get cleaned, roofs that get fixed"
        description="Bookable halls and rooms with clash detection and rate-card pricing, hall-hire income invoiced straight to the property's fund, recurring housekeeping tasks, and maintenance jobs whose actual cost posts to the ledger on completion — with a per-property income and spend account."
      />

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-6">
        <Kpi testId="kpi-properties" label="Properties" value={props.counters.properties}
             accent="sky" icon={Building2} sub={`${props.counters.spaces} bookable spaces`} />
        <Kpi testId="kpi-upcoming" label="Upcoming bookings" value={bookings.counters.upcoming}
             accent="violet" icon={CalendarClock} />
        <Kpi testId="kpi-hire-income" label="Hire income" accent="emerald" icon={Receipt}
             value={money(bookings.counters.income, "INR", true)} />
        <Kpi testId="kpi-open-jobs" label="Open jobs" value={maintenance.counters.open}
             accent="amber" icon={Hammer} sub={`${maintenance.counters.urgent} urgent`} />
        <Kpi testId="kpi-committed" label="Committed spend" accent="rose"
             value={money(maintenance.counters.committed, "INR", true)} />
        <Kpi testId="kpi-housekeeping" label="Housekeeping due" value={housekeeping.counters.due}
             accent="teal" icon={Sparkles} sub={`${housekeeping.counters.overdue} overdue`} />
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="bookings" data-testid="tab-bookings">Bookings</TabsTrigger>
          <TabsTrigger value="calendar" data-testid="tab-calendar">Calendar</TabsTrigger>
          <TabsTrigger value="invoices" data-testid="tab-invoices">Invoices & payments</TabsTrigger>
          <TabsTrigger value="hirers" data-testid="tab-hirers">Hirers</TabsTrigger>
          <TabsTrigger value="utilisation" data-testid="tab-utilisation">Utilisation</TabsTrigger>
          <TabsTrigger value="properties" data-testid="tab-properties">Properties & spaces</TabsTrigger>
          <TabsTrigger value="housekeeping" data-testid="tab-housekeeping">Housekeeping</TabsTrigger>
          <TabsTrigger value="maintenance" data-testid="tab-maintenance">Maintenance</TabsTrigger>
          <TabsTrigger value="requests" data-testid="tab-requests">
            Requests
            {requests.counters.pending > 0 && (
              <span className="ml-1.5 rounded-full bg-amber-500/20 px-1.5 font-mono text-[10px] text-amber-700 dark:text-amber-300"
                    data-testid="requests-badge">{requests.counters.pending}</span>
            )}
          </TabsTrigger>
          <TabsTrigger value="hire" data-testid="tab-hire-settings">Hire settings</TabsTrigger>
        </TabsList>

        <TabsContent value="invoices" className="mt-4">
          <Invoices onOpenBooking={(ref) => {
            const found = bookings.items.find((b) => b.booking_ref === ref);
            if (found) setFinanceFor(found);
          }} />
        </TabsContent>

        <TabsContent value="hirers" className="mt-4">
          <Hirers />
        </TabsContent>

        <TabsContent value="utilisation" className="mt-4">
          <Utilisation />
        </TabsContent>

        <TabsContent value="requests" className="mt-4">
          <HireRequests requests={requests} rooms={props.spaces} canWrite={can("property", "write")}
                        onChanged={load} />
        </TabsContent>

        <TabsContent value="hire" className="mt-4">
          <HireSettings config={hire.values} rooms={hire.rooms} canWrite={can("property", "write")}
                        onSaved={load} />
        </TabsContent>

        <TabsContent value="bookings" className="mt-4">
          <Panel testId="panel-bookings" kicker="FR-PROP-001" title="Hall and room bookings"
                 actions={can("property", "write") && (
                   <div className="flex flex-wrap items-center gap-2">
                   <RecurringBooking spaces={props.spaces} onBooked={load} />
                   <Dialog open={dialog === "bkg"} onOpenChange={(o) => setDialog(o ? "bkg" : "")}>
                     <DialogTrigger asChild>
                       <Button size="sm" data-testid="new-booking-button"
                               className="h-8 rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
                         <Plus className="mr-1 h-3.5 w-3.5" /> New booking
                       </Button>
                     </DialogTrigger>
                     <DialogContent className="max-w-lg border-border bg-card">
                       <DialogHeader><DialogTitle>Book a space</DialogTitle></DialogHeader>
                       <div className="grid grid-cols-2 gap-3">
                         <div className="col-span-2"><F label="Space">
                           <Select value={booking.space_code} onValueChange={(v) => setBooking({ ...booking, space_code: v })}>
                             <SelectTrigger data-testid="booking-space-select" className="bg-muted/40">
                               <SelectValue placeholder="Choose a hall or room" /></SelectTrigger>
                             <SelectContent>{props.spaces.map((s) => (
                               <SelectItem key={s.code} value={s.code}>
                                 {s.name} · seats {s.capacity} · {money(s.hourly_rate, "INR")}/hr
                               </SelectItem>))}</SelectContent>
                           </Select></F></div>
                         <div className="col-span-2"><F label="Purpose"><Input value={booking.title}
                            data-testid="booking-title-input"
                            onChange={(e) => setBooking({ ...booking, title: e.target.value })}
                            className="bg-muted/40" /></F></div>
                         <F label="Booked for"><Input value={booking.booked_for}
                            data-testid="booking-for-input"
                            onChange={(e) => setBooking({ ...booking, booked_for: e.target.value })}
                            className="bg-muted/40" /></F>
                         <F label="Type">
                           <Select value={booking.booking_type} onValueChange={(v) => setBooking({ ...booking, booking_type: v })}>
                             <SelectTrigger data-testid="booking-type-select" className="bg-muted/40"><SelectValue /></SelectTrigger>
                             <SelectContent><SelectItem value="external">External (chargeable)</SelectItem>
                               <SelectItem value="internal">Internal (free)</SelectItem></SelectContent>
                           </Select>
                         </F>
                         <F label="Starts">
                           <div className="flex gap-1.5">
                             <DateField value={(booking.starts_at || "").slice(0, 10)}
                                        testId="booking-start-input" clearable={false}
                                        onChange={(v) => setBooking({ ...booking,
                                          starts_at: `${v}T${(booking.starts_at || "").slice(11, 16) || "09:00"}`,
                                          ends_at: `${v}T${(booking.ends_at || "").slice(11, 16) || "12:00"}` })}
                                        className="h-9 flex-1 text-[11.5px]" />
                             <Input type="time" data-testid="booking-start-time"
                                    value={(booking.starts_at || "").slice(11, 16)}
                                    onChange={(e) => setBooking({ ...booking,
                                      starts_at: `${(booking.starts_at || "").slice(0, 10)
                                        || new Date().toISOString().slice(0, 10)}T${e.target.value}` })}
                                    className="h-9 w-[92px] bg-muted/40 font-mono text-[11.5px]" />
                           </div>
                         </F>
                         <F label="Ends">
                           <div className="flex gap-1.5">
                             <DateField value={(booking.ends_at || "").slice(0, 10)}
                                        testId="booking-end-input" clearable={false}
                                        onChange={(v) => setBooking({ ...booking,
                                          ends_at: `${v}T${(booking.ends_at || "").slice(11, 16) || "12:00"}` })}
                                        className="h-9 flex-1 text-[11.5px]" />
                             <Input type="time" data-testid="booking-end-time"
                                    value={(booking.ends_at || "").slice(11, 16)}
                                    onChange={(e) => setBooking({ ...booking,
                                      ends_at: `${(booking.ends_at || booking.starts_at || "").slice(0, 10)
                                        || new Date().toISOString().slice(0, 10)}T${e.target.value}` })}
                                    className="h-9 w-[92px] bg-muted/40 font-mono text-[11.5px]" />
                           </div>
                         </F>
                         <F label="Attendees"><Input type="number" value={booking.attendees}
                            data-testid="booking-attendees-input"
                            onChange={(e) => setBooking({ ...booking, attendees: Number(e.target.value) })}
                            className="bg-muted/40 font-mono" /></F>
                         <F label="Contact"><Input value={booking.contact} data-testid="booking-contact-input"
                            onChange={(e) => setBooking({ ...booking, contact: e.target.value })}
                            className="bg-muted/40 font-mono text-[12px]" /></F>
                       </div>
                       <Button data-testid="booking-save-button"
                               disabled={busy === "bkg" || !booking.space_code || !booking.title
                                 || booking.starts_at.length < 16 || booking.ends_at.length < 16}
                               onClick={() => act("bkg", () => api.post("/property/bookings", booking),
                                 (r) => r.data.charge > 0
                                   ? `${r.data.booking_ref} booked · ${money(r.data.charge, "INR")} to invoice`
                                   : `${r.data.booking_ref} booked (internal, no charge)`)
                                 .then(() => setBooking(BLANK_BKG))}
                               className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
                         {busy === "bkg" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Confirm booking
                       </Button>
                     </DialogContent>
                   </Dialog>
                   </div>
                 )}>
            <Table testId="bookings-table"
                   head={<><Th>Reference</Th><Th>Space</Th><Th>Purpose</Th><Th>When</Th><Th>Hours</Th>
                     <Th>Charge</Th><Th>Invoice</Th><Th /></>}>
              {bookings.items.length === 0 && <Empty colSpan={8} label="No bookings yet" />}
              {bookings.items.map((b) => (
                <tr key={refOf(b)} className="row-hover cursor-pointer" onClick={rowClick(() => detail.show(b, refOf(b), "", "", "booking"))} data-testid={`booking-row-${b.booking_ref}`}>
                  <Td className="font-mono text-[11px]">{b.booking_ref}</Td>
                  <Td><div className="font-medium text-foreground">{b.space_name}</div>
                    <div className="font-mono text-[10.5px] text-muted-foreground">{b.property_code}</div></Td>
                  <Td className="text-[11.5px]">{b.title}
                    <div className="text-[10.5px] text-muted-foreground">{b.booked_for}</div></Td>
                  <Td className="font-mono text-[10.5px]">{dateTime(b.starts_at)}</Td>
                  <Td className="font-mono text-[11px]">{b.hours}</Td>
                  <Td className="font-mono text-[11px]">
                    {b.status === "cancelled"
                      ? (b.cancellation_charge > 0 ? money(b.cancellation_charge, b.currency) : "—")
                      : (b.charge > 0 ? money(b.charge, b.currency) : "—")}
                    {b.status === "cancelled" && b.cancellation_charge > 0 && (
                      <div className="text-[10px] text-muted-foreground">cancellation charge</div>
                    )}</Td>
                  <Td><Chip testId={`booking-invoice-${b.booking_ref}`}
                            className={b.invoice_status === "invoiced" ? OK
                              : b.invoice_status === "pending" ? WARN : INFO}>
                    {(b.invoice_status || "").replace("_", " ")}</Chip>
                    {b.charge > 0 && (
                      <div className="mt-0.5 font-mono text-[10px] text-muted-foreground"
                           data-testid={`booking-balance-${b.booking_ref}`}>
                        {money(b.hire_paid || 0, b.currency)} paid
                      </div>
                    )}</Td>
                  <Td className="space-x-1.5" onClick={stopRow}>
                    {b.charge > 0 && (
                      <Button size="sm" variant="outline" data-testid={`money-${b.booking_ref}`}
                              onClick={() => setFinanceFor(b)}
                              className="h-7 rounded-full border-border px-2.5 text-[10px]">Money</Button>
                    )}
                    {can("volunteer", "write") && (
                      <Button size="sm" variant="outline" data-testid={`booking-log-time-${b.booking_ref}`}
                              onClick={() => setTimeForBooking(b)}
                              className="h-7 rounded-full border-border px-2.5 text-[10px]">Time</Button>
                    )}
                    {b.invoice_status === "pending" && can("property", "approve") && (
                      <Button size="sm" variant="outline" data-testid={`invoice-${b.booking_ref}`}
                              disabled={busy === `inv-${b.booking_ref}`}
                              onClick={() => act(`inv-${b.booking_ref}`,
                                () => api.post(`/property/bookings/${b.booking_ref}/invoice`),
                                (r) => `${r.data.invoice_ref} · ${money(r.data.charge, "INR")} posted to ${r.data.fund_code}, due ${r.data.due_on}`)}
                              className="h-7 rounded-full border-border px-2.5 text-[10px]">Invoice</Button>
                    )}
                    {b.status !== "cancelled" && b.invoice_status !== "invoiced" && can("property", "write") && (
                      <Button size="sm" variant="outline" data-testid={`cancel-${b.booking_ref}`}
                              onClick={() => setCancellingBooking(b)}
                              className="h-7 rounded-full border-border px-2.5 text-[10px]">Cancel</Button>
                    )}
                    {b.status === "cancelled" && <Chip className={BAD}>cancelled</Chip>}
                  </Td>
                </tr>
              ))}
            </Table>
          </Panel>
        </TabsContent>

        <TabsContent value="calendar" className="mt-4">
          <BookingCalendar spaces={props.spaces} bookings={bookings.items}
                           requests={requests.items}
                           onOpen={(b) => detail.show(b, refOf(b), "", "", "booking")}
                           onOpenRequest={() => setTab("requests")} />
        </TabsContent>

        <TabsContent value="properties" className="mt-4 space-y-4">
          <Panel testId="panel-properties" kicker="Estate" title="Properties">
            <Table testId="properties-table"
                   head={<><Th>Property</Th><Th>Type</Th><Th>City</Th><Th>Tenure</Th><Th>Spaces</Th>
                     <Th>Bookings</Th><Th>Hire income</Th><Th>Open work</Th><Th /></>}>
              {props.items.map((p) => (
                <tr key={refOf(p)} className="row-hover cursor-pointer" onClick={rowClick(() => detail.show(p, refOf(p), "", "", "property"))} data-testid={`property-row-${p.code}`}>
                  <Td><div className="font-medium text-foreground">{p.name}</div>
                    <div className="font-mono text-[10.5px] text-muted-foreground">{p.code}</div></Td>
                  <Td className="text-[11px]">{(p.property_type || "").replace("_", " ")}</Td>
                  <Td className="text-[11px]">{p.city}</Td>
                  <Td className="text-[11px]">{p.tenure}</Td>
                  <Td className="font-mono text-[11px]">{p.spaces}</Td>
                  <Td className="font-mono text-[11px]">{p.bookings}</Td>
                  <Td className="font-mono text-[11px]">{money(p.income, "INR", true)}</Td>
                  <Td className="text-[11px]">
                    {p.open_jobs} jobs · {p.due_tasks} tasks
                  </Td>
                  <Td>
                    <Button size="sm" variant="outline" data-testid={`ledger-${p.code}`}
                            onClick={async () => {
                              const r = await api.get(`/property/ledger/${p.code}`);
                              setLedger({ ...r.data, name: p.name });
                            }}
                            className="h-7 rounded-full border-border px-2.5 text-[10px]">Ledger</Button>
                  </Td>
                </tr>
              ))}
            </Table>
          </Panel>

          <Panel testId="panel-spaces" kicker="Rate card" title="Rooms, halls and other spaces"
                 actions={can("property", "write") && (
                   <Dialog open={dialog === "space"} onOpenChange={(o) => setDialog(o ? "space" : "")}>
                     <DialogTrigger asChild>
                       <Button size="sm" data-testid="new-space-button"
                               className="h-8 rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
                         <Plus className="mr-1 h-3.5 w-3.5" /> Add a room or hall
                       </Button>
                     </DialogTrigger>
                     <DialogContent className="max-w-lg border-border bg-card">
                       <DialogHeader><DialogTitle>Add a room, hall or other space</DialogTitle></DialogHeader>
                       <div className="grid grid-cols-2 gap-3">
                         <div className="col-span-2"><F label="Property">
                           <Select value={space.property_code}
                                   onValueChange={(v) => setSpace({ ...space, property_code: v })}>
                             <SelectTrigger data-testid="space-property-select" className="bg-muted/40">
                               <SelectValue placeholder="Which building is it in?" /></SelectTrigger>
                             <SelectContent>{props.items.map((p) => (
                               <SelectItem key={p.code} value={p.code}>{p.name}</SelectItem>))}</SelectContent>
                           </Select></F></div>
                         <F label="Name"><Input value={space.name} data-testid="space-name-input"
                            onChange={(e) => setSpace({ ...space, name: e.target.value })}
                            className="bg-muted/40" /></F>
                         <F label="Room / hall number"><Input value={space.room_number}
                            data-testid="space-number-input"
                            onChange={(e) => setSpace({ ...space, room_number: e.target.value })}
                            className="bg-muted/40 font-mono" /></F>
                         <F label="Type">
                           <Select value={space.space_type}
                                   onValueChange={(v) => setSpace({ ...space, space_type: v })}>
                             <SelectTrigger data-testid="space-type-select" className="bg-muted/40"><SelectValue /></SelectTrigger>
                             <SelectContent>{["hall", "room", "classroom", "office", "desk", "bed",
                               "pitch", "kitchen", "storage"].map((x) => (
                               <SelectItem key={x} value={x}>{x}</SelectItem>))}</SelectContent>
                           </Select>
                         </F>
                         <F label="Capacity"><Input type="number" value={space.capacity}
                            data-testid="space-capacity-input"
                            onChange={(e) => setSpace({ ...space, capacity: Number(e.target.value) })}
                            className="bg-muted/40 font-mono" /></F>
                         <F label="Hourly rate"><Input type="number" value={space.hourly_rate}
                            data-testid="space-hourly-input"
                            onChange={(e) => setSpace({ ...space, hourly_rate: Number(e.target.value) })}
                            className="bg-muted/40 font-mono" /></F>
                         <F label="Day rate"><Input type="number" value={space.day_rate}
                            data-testid="space-day-input"
                            onChange={(e) => setSpace({ ...space, day_rate: Number(e.target.value) })}
                            className="bg-muted/40 font-mono" /></F>
                         <div className="col-span-2"><F label="Notes"><Textarea rows={2}
                            value={space.notes} data-testid="space-notes-input"
                            onChange={(e) => setSpace({ ...space, notes: e.target.value })}
                            className="bg-muted/40 text-[12px]" /></F></div>
                         <div className="col-span-2 space-y-2 rounded-lg border border-border bg-muted/25 p-3">
                           <div className="text-[11.5px] font-medium text-foreground">
                             Facility policies shown to hirers
                           </div>
                           <div className="grid grid-cols-2 gap-2.5">
                             {[["pet_friendly", "Pets", ["allowed", "not_allowed", "subject_to_availability"]],
                               ["smoking_policy", "Smoking", ["allowed", "not_allowed"]],
                               ["food_allowed", "Food", ["allowed", "not_allowed", "optional"]],
                               ["fireplace_allowed", "Fireplace", ["available", "not_available", "allowed", "not_allowed"]],
                               ["kids_friendly", "Children", ["allowed", "not_allowed"]],
                               ["kids_play_area", "Kids play area", ["available", "not_available", "included", "chargeable"]],
                               ["parking_available", "Parking", ["available", "not_available", "subject_to_availability"]],
                               ["parking_included", "Parking cost", ["included", "chargeable", "optional", "not_applicable"]],
                             ].map(([key, label, options]) => (
                               <F key={key} label={label}>
                                 <Select value={space[key]}
                                         onValueChange={(v) => setSpace({ ...space, [key]: v })}>
                                   <SelectTrigger data-testid={`space-${key}-select`}
                                                  className="bg-muted/40 text-[11.5px]">
                                     <SelectValue />
                                   </SelectTrigger>
                                   <SelectContent>
                                     {options.map((o) => (
                                       <SelectItem key={o} value={o}>{o.replace(/_/g, " ")}</SelectItem>
                                     ))}
                                   </SelectContent>
                                 </Select>
                               </F>
                             ))}
                             <F label="Parking spaces">
                               <Input type="number" value={space.parking_spaces}
                                      data-testid="space-parking-spaces-input"
                                      onChange={(e) => setSpace({ ...space, parking_spaces: Number(e.target.value) })}
                                      className="bg-muted/40 font-mono" />
                             </F>
                             <F label="Parking charge">
                               <Input type="number" value={space.parking_charge}
                                      data-testid="space-parking-charge-input"
                                      onChange={(e) => setSpace({ ...space, parking_charge: Number(e.target.value) })}
                                      className="bg-muted/40 font-mono" />
                             </F>
                             <div className="col-span-2"><F label="Policy notes and conditions">
                               <Textarea rows={2} value={space.policy_notes}
                                         data-testid="space-policy-notes-input"
                                         onChange={(e) => setSpace({ ...space, policy_notes: e.target.value })}
                                         className="bg-muted/40 text-[12px]" /></F></div>
                           </div>
                         </div>
                       </div>
                       <Button data-testid="space-save-button"
                               disabled={busy === "space" || !space.property_code || !space.name}
                               onClick={() => act("space", () => api.post("/property/spaces", space),
                                 (r) => `${r.data.name} added as ${r.data.code}`)
                                 .then(() => setSpace(BLANK_SPACE))}
                               className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
                         {busy === "space" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Add the space
                       </Button>
                     </DialogContent>
                   </Dialog>
                 )}>
            <Table testId="spaces-table"
                   head={<><Th>Space</Th><Th>Number</Th><Th>Property</Th><Th>Type</Th><Th>Capacity</Th>
                     <Th>Hourly</Th><Th>Day rate</Th></>}>
              {props.spaces.length === 0 && <Empty colSpan={7} label="No rooms or halls added yet" />}
              {props.spaces.map((s) => (
                <tr key={refOf(s)} className="row-hover cursor-pointer" onClick={rowClick(() => detail.show(s, refOf(s), "", "", "space"))} data-testid={`space-row-${s.code}`}>
                  <Td className="font-medium text-foreground">{s.name}</Td>
                  <Td className="font-mono text-[11px]">{s.room_number || "—"}</Td>
                  <Td className="font-mono text-[11px]">{s.property_code}</Td>
                  <Td className="text-[11px]">{(s.space_type || "").replace("_", " ")}</Td>
                  <Td className="font-mono text-[11px]">{s.capacity}</Td>
                  <Td className="font-mono text-[11px]">{money(s.hourly_rate, s.currency)}</Td>
                  <Td className="font-mono text-[11px]">{money(s.day_rate, s.currency)}</Td>
                </tr>
              ))}
            </Table>
            <div className="border-t border-border px-4 py-2.5 text-[11px] text-muted-foreground">
              Click any row to open it, then use Edit to change the name, number, capacity or rate —
              or Delete to remove a space that has no live bookings.
            </div>
          </Panel>
        </TabsContent>

        <TabsContent value="housekeeping" className="mt-4">
          <Panel testId="panel-housekeeping" kicker="FR-PROP-005" title="Housekeeping schedule"
                 actions={can("property", "write") && (
                   <Dialog open={dialog === "task"} onOpenChange={(o) => setDialog(o ? "task" : "")}>
                     <DialogTrigger asChild>
                       <Button size="sm" data-testid="new-task-button"
                               className="h-8 rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
                         <Plus className="mr-1 h-3.5 w-3.5" /> Add task
                       </Button>
                     </DialogTrigger>
                     <DialogContent className="max-w-md border-border bg-card">
                       <DialogHeader><DialogTitle>Add a housekeeping task</DialogTitle></DialogHeader>
                       <div className="space-y-3">
                         <F label="Property">
                           <Select value={task.property_code} onValueChange={(v) => setTask({ ...task, property_code: v })}>
                             <SelectTrigger data-testid="task-property-select" className="bg-muted/40">
                               <SelectValue placeholder="Property" /></SelectTrigger>
                             <SelectContent>{props.items.map((p) => (
                               <SelectItem key={p.code} value={p.code}>{p.name}</SelectItem>))}</SelectContent>
                           </Select>
                         </F>
                         <F label="Task"><Input value={task.task} data-testid="task-name-input"
                            onChange={(e) => setTask({ ...task, task: e.target.value })}
                            className="bg-muted/40" /></F>
                         <div className="grid grid-cols-2 gap-3">
                           <F label="Frequency">
                             <Select value={task.frequency} onValueChange={(v) => setTask({ ...task, frequency: v })}>
                               <SelectTrigger data-testid="task-frequency-select" className="bg-muted/40"><SelectValue /></SelectTrigger>
                               <SelectContent>{["daily", "weekly", "monthly", "quarterly", "per_booking"].map((x) => (
                                 <SelectItem key={x} value={x}>{x.replace("_", " ")}</SelectItem>))}</SelectContent>
                             </Select>
                           </F>
                           <F label="Due on"><Input type="date" value={task.due_on} data-testid="task-due-input"
                              onChange={(e) => setTask({ ...task, due_on: e.target.value })}
                              className="bg-muted/40 text-[12px]" /></F>
                         </div>
                       </div>
                       <Button data-testid="task-save-button"
                               disabled={busy === "task" || !task.property_code || !task.task}
                               onClick={() => act("task", () => api.post("/property/housekeeping",
                                 { ...task, due_on: task.due_on || null }), "Task scheduled")
                                 .then(() => setTask(BLANK_TASK))}
                               className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
                         {busy === "task" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Schedule
                       </Button>
                     </DialogContent>
                   </Dialog>
                 )}>
            <Table testId="housekeeping-table"
                   head={<><Th>Reference</Th><Th>Property</Th><Th>Task</Th><Th>Frequency</Th>
                     <Th>Due</Th><Th>Status</Th><Th /></>}>
              {housekeeping.items.length === 0 && <Empty colSpan={7} />}
              {housekeeping.items.map((t) => (
                <tr key={refOf(t)} className="row-hover cursor-pointer" onClick={rowClick(() => detail.show(t, refOf(t)))} data-testid={`task-row-${t.task_ref}`}>
                  <Td className="font-mono text-[11px]">{t.task_ref}</Td>
                  <Td className="font-mono text-[11px]">{t.property_code}</Td>
                  <Td className="text-[11.5px] text-foreground">{t.task}</Td>
                  <Td className="text-[11px]">{(t.frequency || "").replace("_", " ")}</Td>
                  <Td className="font-mono text-[11px]">{t.due_on}</Td>
                  <Td><Chip testId={`task-status-${t.task_ref}`}
                            className={t.status === "done" ? OK : t.status === "in_progress" ? INFO : WARN}>
                    {(t.status || "").replace("_", " ")}</Chip></Td>
                  <Td>
                    {t.status !== "done" && can("property", "write") && (
                      <Select value="" onValueChange={(v) => act(`t-${t.task_ref}`,
                        () => api.post(`/property/housekeeping/${t.task_ref}/status`, { status: v }),
                        `Task marked ${v.replace("_", " ")}`)}>
                        <SelectTrigger data-testid={`task-move-${t.task_ref}`} className="h-7 w-[120px] bg-muted/40 text-[11px]">
                          <SelectValue placeholder="Update" /></SelectTrigger>
                        <SelectContent>{housekeeping.states.filter((s) => s !== t.status).map((s) => (
                          <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>))}</SelectContent>
                      </Select>
                    )}
                  </Td>
                </tr>
              ))}
            </Table>
          </Panel>
        </TabsContent>

        <TabsContent value="maintenance" className="mt-4">
          <Panel testId="panel-maintenance" kicker="FR-PROP-005" title="Maintenance jobs"
                 actions={can("property", "write") && (
                   <Dialog open={dialog === "job"} onOpenChange={(o) => setDialog(o ? "job" : "")}>
                     <DialogTrigger asChild>
                       <Button size="sm" data-testid="new-job-button"
                               className="h-8 rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
                         <Plus className="mr-1 h-3.5 w-3.5" /> Report a job
                       </Button>
                     </DialogTrigger>
                     <DialogContent className="max-w-lg border-border bg-card">
                       <DialogHeader><DialogTitle>Report a maintenance job</DialogTitle></DialogHeader>
                       <div className="grid grid-cols-2 gap-3">
                         <div className="col-span-2"><F label="Property">
                           <Select value={job.property_code} onValueChange={(v) => setJob({ ...job, property_code: v })}>
                             <SelectTrigger data-testid="job-property-select" className="bg-muted/40">
                               <SelectValue placeholder="Property" /></SelectTrigger>
                             <SelectContent>{props.items.map((p) => (
                               <SelectItem key={p.code} value={p.code}>{p.name}</SelectItem>))}</SelectContent>
                           </Select></F></div>
                         <div className="col-span-2"><F label="What needs doing"><Input value={job.title}
                            data-testid="job-title-input"
                            onChange={(e) => setJob({ ...job, title: e.target.value })}
                            className="bg-muted/40" /></F></div>
                         <F label="Category">
                           <Select value={job.category} onValueChange={(v) => setJob({ ...job, category: v })}>
                             <SelectTrigger data-testid="job-category-select" className="bg-muted/40"><SelectValue /></SelectTrigger>
                             <SelectContent>{["repair", "refurbishment", "compliance", "grounds", "safety"].map((x) => (
                               <SelectItem key={x} value={x}>{x}</SelectItem>))}</SelectContent>
                           </Select>
                         </F>
                         <F label="Priority">
                           <Select value={job.priority} onValueChange={(v) => setJob({ ...job, priority: v })}>
                             <SelectTrigger data-testid="job-priority-select" className="bg-muted/40"><SelectValue /></SelectTrigger>
                             <SelectContent>{["low", "medium", "high"].map((x) => (
                               <SelectItem key={x} value={x}>{x}</SelectItem>))}</SelectContent>
                           </Select>
                         </F>
                         <F label="Contractor"><Input value={job.contractor} data-testid="job-contractor-input"
                            onChange={(e) => setJob({ ...job, contractor: e.target.value })}
                            className="bg-muted/40" /></F>
                         <F label="Estimated cost"><Input type="number" value={job.estimated_cost}
                            data-testid="job-cost-input"
                            onChange={(e) => setJob({ ...job, estimated_cost: Number(e.target.value) })}
                            className="bg-muted/40 font-mono" /></F>
                         <div className="col-span-2"><F label="Detail"><Textarea rows={2}
                            value={job.description} data-testid="job-description-input"
                            onChange={(e) => setJob({ ...job, description: e.target.value })}
                            className="bg-muted/40 text-[12px]" /></F></div>
                       </div>
                       <Button data-testid="job-save-button"
                               disabled={busy === "job" || !job.property_code || !job.title}
                               onClick={() => act("job", () => api.post("/property/maintenance",
                                 { ...job, due_on: job.due_on || null }), "Job logged")
                                 .then(() => setJob(BLANK_JOB))}
                               className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
                         {busy === "job" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Log job
                       </Button>
                     </DialogContent>
                   </Dialog>
                 )}>
            <Table testId="maintenance-table"
                   head={<><Th>Reference</Th><Th>Property</Th><Th>Job</Th><Th>Priority</Th><Th>Contractor</Th>
                     <Th>Estimate</Th><Th>Actual</Th><Th>Status</Th><Th /></>}>
              {maintenance.items.length === 0 && <Empty colSpan={9} />}
              {maintenance.items.map((j) => (
                <tr key={j.job_ref} className="row-hover cursor-pointer"
                    onClick={() => detail.show(j, j.job_ref, j.title, "Maintenance job")}
                    data-testid={`job-row-${j.job_ref}`}>
                  <Td className="font-mono text-[11px]">{j.job_ref}</Td>
                  <Td className="font-mono text-[11px]">{j.property_code}</Td>
                  <Td className="text-[11.5px] text-foreground">{j.title}
                    <div className="text-[10.5px] text-muted-foreground">due {j.due_on}</div></Td>
                  <Td><Chip className={j.priority === "high" ? BAD : j.priority === "medium" ? WARN : INFO}>
                    {j.priority}</Chip></Td>
                  <Td className="text-[11px]">{j.contractor || "—"}</Td>
                  <Td className="font-mono text-[11px]">{money(j.estimated_cost, "INR", true)}</Td>
                  <Td className="font-mono text-[11px]">{j.actual_cost ? money(j.actual_cost, "INR", true) : "—"}</Td>
                  <Td><Chip testId={`job-status-${j.job_ref}`}
                            className={j.status === "completed" ? OK : j.status === "cancelled" ? BAD
                              : j.status === "in_progress" ? INFO : WARN}>
                    {(j.status || "").replace("_", " ")}</Chip></Td>
                  <Td onClick={stopRow}>
                    {!["completed", "cancelled"].includes(j.status) && can("property", "write") && (
                      <div className="flex items-center gap-1.5">
                      <Select value="" onValueChange={(v) => {
                        if (v === "completed") {
                          setCompleting(j);
                          setCompletion({ actual_cost: j.estimated_cost, note: "" });
                          return;
                        }
                        act(`j-${j.job_ref}`,
                          () => api.post(`/property/maintenance/${j.job_ref}/status`, { status: v }),
                          `Job moved to ${v.replace("_", " ")}`);
                      }}>
                        <SelectTrigger data-testid={`job-move-${j.job_ref}`} className="h-7 w-[120px] bg-muted/40 text-[11px]">
                          <SelectValue placeholder="Update" /></SelectTrigger>
                        <SelectContent>{maintenance.states.filter((s) => s !== j.status).map((s) => (
                          <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>))}</SelectContent>
                      </Select>
                      <Button size="sm" variant="outline" data-testid={`cancel-job-${j.job_ref}`}
                              onClick={() => { setCancelling(j); setCancelReason(""); }}
                              className="h-7 rounded-full border-red-500/40 px-2.5 text-[10px] text-red-600 dark:text-red-400">
                        Cancel
                      </Button>
                      </div>
                    )}
                  </Td>
                </tr>
              ))}
            </Table>
            <div className="border-t border-border px-4 py-2.5 text-[11px] text-muted-foreground">
              Completing a job posts its actual cost to the property's fund as Property Repairs &
              Maintenance, so the property ledger and the fund balance always agree.
            </div>
          </Panel>
        </TabsContent>
      </Tabs>

      <Dialog open={Boolean(completing)} onOpenChange={(o) => !o && setCompleting(null)}>
        <DialogContent className="max-w-md border-border bg-card" data-testid="complete-job-dialog">
          <DialogHeader><DialogTitle>Close {completing?.job_ref}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-[12px] text-muted-foreground">
              {completing?.title} · estimated {money(completing?.estimated_cost || 0, "INR")}.
              The actual cost posts to the property's fund as Property Repairs & Maintenance.
            </p>
            <F label="Actual cost invoiced">
              <Input type="number" value={completion.actual_cost} data-testid="complete-job-cost-input"
                     onChange={(e) => setCompletion({ ...completion, actual_cost: Number(e.target.value) })}
                     className="bg-muted/40 font-mono" />
            </F>
            <F label="Closing note">
              <Textarea rows={2} value={completion.note} data-testid="complete-job-note-input"
                        onChange={(e) => setCompletion({ ...completion, note: e.target.value })}
                        className="bg-muted/40 text-[12px]" />
            </F>
            <Button data-testid="complete-job-button" disabled={busy === "complete" || completion.actual_cost < 0}
                    onClick={() => act("complete", () => api.post(
                      `/property/maintenance/${completing.job_ref}/status`,
                      { status: "completed", actual_cost: completion.actual_cost, note: completion.note }),
                      (r) => r.data.posted
                        ? `Closed · ${money(r.data.posted.amount, "INR")} posted to ${r.data.posted.fund_code}`
                        : "Job closed")
                      .then(() => setCompleting(null))}
                    className="w-full rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
              {busy === "complete" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Close the job
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(ledger)} onOpenChange={(o) => !o && setLedger(null)}>
        <DialogContent className="max-w-2xl border-border bg-card" data-testid="property-ledger-dialog">
          <DialogHeader><DialogTitle>{ledger?.name} · property account</DialogTitle></DialogHeader>
          {ledger && (
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-3">
                {[["Income", ledger.income], ["Spend", ledger.spend], ["Net", ledger.net]].map(([k, v]) => (
                  <div key={k} className="rounded-xl border border-border bg-muted/25 p-3">
                    <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{k}</div>
                    <div className="text-[15px] font-bold text-foreground">{money(v, "INR", true)}</div>
                  </div>
                ))}
              </div>
              <div className="max-h-[45vh] overflow-y-auto">
                <Table testId="property-ledger-table"
                       head={<><Th>Date</Th><Th>Account</Th><Th>Description</Th><Th>Dr/Cr</Th><Th>Amount</Th></>}>
                  {ledger.entries.length === 0 && <Empty colSpan={5} label="Nothing posted yet" />}
                  {ledger.entries.map((e, i) => (
                    <tr key={`${e.entry_date}-${i}`} className="row-hover cursor-pointer"
                        onClick={rowClick(() => detail.show(e, refOf(e), e.account_name, "Ledger entry"))}
                        data-testid={`property-ledger-row-${i}`}>
                      <Td className="font-mono text-[11px]">{e.entry_date}</Td>
                      <Td className="text-[11px]">{e.account_name}</Td>
                      <Td className="text-[11px] text-muted-foreground">{e.description}</Td>
                      <Td><Chip className={e.direction === "credit" ? OK : WARN}>{e.direction}</Chip></Td>
                      <Td className="font-mono text-[11px]">{money(e.base_amount, "INR")}</Td>
                    </tr>
                  ))}
                </Table>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      <Dialog open={!!cancelling} onOpenChange={(o) => !o && setCancelling(null)}>
        <DialogContent className="max-w-md border-border bg-card">
          <DialogHeader>
            <DialogTitle>Cancel {cancelling?.job_ref}</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-muted-foreground">
            {cancelling?.title} · {cancelling?.property_code}. The job stays on the register as
            cancelled and its {money(cancelling?.estimated_cost || 0, "INR", true)} estimate is
            released back to the budget line.
          </p>
          <Textarea data-testid="cancel-job-reason" rows={3} value={cancelReason}
                    onChange={(e) => setCancelReason(e.target.value)}
                    placeholder="Why is it being cancelled? (at least 10 characters)"
                    className="bg-muted/40 text-sm" />
          <Button data-testid="cancel-job-submit"
                  disabled={busy === "cancel" || cancelReason.trim().length < 10}
                  onClick={() => act("cancel",
                    () => api.post(`/property/maintenance/${cancelling.job_ref}/cancel`,
                                   { reason: cancelReason }),
                    "Job cancelled and the commitment released")
                    .then(() => setCancelling(null))}
                  className="h-9 rounded-full bg-red-600 text-white hover:bg-red-600/90">
            {busy === "cancel" ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : null}
            Cancel the job
          </Button>
        </DialogContent>
      </Dialog>

      {financeFor && (
        <BookingFinance booking={financeFor} canWrite={can("property", "write")}
                        canApprove={can("property", "approve")}
                        onOpenChange={(o) => !o && setFinanceFor(null)} onChanged={load} />
      )}
      {cancellingBooking && (
        <CancelBooking booking={cancellingBooking}
                       onOpenChange={(o) => !o && setCancellingBooking(null)}
                       onCancelled={load} />
      )}

      <LogTimeDialog open={Boolean(timeForBooking)}
                     onOpenChange={(o) => !o && setTimeForBooking(null)}
                     title={`Log time · ${timeForBooking?.title || ""}`}
                     fixed={timeForBooking ? { activity: "booking",
                       reference: timeForBooking.booking_ref } : {}} />

      {detail.sheet}
    </div>
  );
}
